import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Seo, breadcrumbSchema } from "../components/site/Seo";
import { PageHero } from "../components/site/PageHero";
import { PostCard } from "../components/home/Features";
import { CtaBand } from "../components/site/Cta";
import { api } from "../lib/api";
import { SITE } from "../lib/site";
import NotFound from "./NotFound";

export function Blog() {
  const [posts, setPosts] = useState(null);
  useEffect(() => {
    let alive = true;
    api.get("/blog").then((r) => { if (alive) setPosts(Array.isArray(r.data) ? r.data : []); }).catch(() => { if (alive) setPosts([]); });
    return () => { alive = false; };
  }, []);
  const crumbs = [{ to: "/", label: "Accueil" }, { to: "/blog", label: "Blog" }];
  return (
    <>
      <Seo title="Blog pare-brise et vitrage automobile | A+ Glass Haguenau" description="Conseils pratiques sur le pare-brise, le vitrage automobile, la calibration ADAS, la rénovation de phares et l'assurance, par A+ Glass Haguenau." path="/blog" schemas={[breadcrumbSchema(crumbs)]} />
      <PageHero eyebrow="Blog" title="Conseils pare-brise & vitrage" crumbs={crumbs} intro="Nos conseils pour mieux comprendre et entretenir les vitrages et l'éclairage de votre véhicule." />
      <section className="px-5 py-16 lg:px-8">
        <div className="mx-auto grid max-w-[1320px] gap-4 md:grid-cols-2 lg:grid-cols-3" data-testid="blog-list">
          {posts === null && <p className="text-slate-500">Chargement…</p>}
          {posts?.length === 0 && <p className="text-slate-500">Aucun article pour le moment.</p>}
          {posts?.map((p, i) => <PostCard key={p.slug} p={p} i={i % 3} />)}
        </div>
      </section>
    </>
  );
}

function Body({ content }) {
  const blocks = [];
  let list = [];
  const flush = () => { if (list.length) { blocks.push(<ul key={`u${blocks.length}`} className="my-5 list-disc space-y-2 pl-6">{list}</ul>); list = []; } };
  content.split("\n").forEach((raw, i) => {
    const l = raw.trim();
    if (l.startsWith("- ")) { list.push(<li key={i}>{l.slice(2)}</li>); return; }
    flush();
    if (l.startsWith("## ")) blocks.push(<h2 key={i} className="mt-10 font-display text-2xl font-extrabold text-ag-ink sm:text-3xl">{l.slice(3)}</h2>);
    else if (l) blocks.push(<p key={i} className="mt-4">{l}</p>);
  });
  flush();
  return <div className="text-lg leading-relaxed text-slate-700">{blocks}</div>;
}

export function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(undefined);
  useEffect(() => {
    let alive = true;
    setPost(undefined);
    api.get(`/blog/${slug}`).then((r) => { if (alive) setPost(r.data || null); }).catch(() => { if (alive) setPost(null); });
    return () => { alive = false; };
  }, [slug]);
  if (post === null) return <NotFound />;
  if (!post) return <div className="min-h-screen bg-ag-mist pt-40 text-center text-slate-500">Chargement…</div>;
  const crumbs = [{ to: "/", label: "Accueil" }, { to: "/blog", label: "Blog" }, { to: `/blog/${post.slug}`, label: post.title }];
  const article = { "@context": "https://schema.org", "@type": "BlogPosting", headline: post.title, description: post.meta_description || post.excerpt, datePublished: post.created_at, dateModified: post.updated_at, author: { "@type": "Organization", name: SITE.name }, image: `${window.location.origin}${post.cover}` };
  return (
    <>
      <Seo title={`${post.title} | A+ Glass Haguenau`} description={post.meta_description || post.excerpt} path={`/blog/${post.slug}`} image={post.cover} type="article" schemas={[article, breadcrumbSchema(crumbs)]} />
      <PageHero eyebrow={new Date(post.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })} title={post.title} crumbs={crumbs} intro={post.excerpt} />
      <article className="px-5 py-16 lg:px-8" data-testid="blog-article">
        <div className="mx-auto max-w-[760px]">
          <div className="mb-10 aspect-[16/9] overflow-hidden rounded-[1.75rem] bg-ag-ink"><img src={post.cover} alt={post.title} className="h-full w-full object-cover" /></div>
          <Body content={post.content} />
          <Link to="/blog" className="btn btn-outline mt-12" data-testid="blog-back-link"><ArrowLeft className="h-4 w-4" />Tous les articles</Link>
        </div>
      </article>
      <CtaBand />
    </>
  );
}
